import { Injectable } from '@angular/core';
import { Observable, from, of, forkJoin } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';
import { ItemDetailsService } from '../../services/item-details.service';
import {
    ExcelImportContext,
    ExcelImportResult,
    ExcelSkippedRow,
    ParsedExcelRow,
    STOCK_CHECK_EXCLUDED_ITEMS,
    applyRunningStockCheck,
    buildImportedOrderLine,
    buildStockLedger,
    parseExcelWorkbook,
    readFileAsArrayBuffer,
    resolveItemNameFromItemDetails,
    getItemStatusRejectionMessage,
    resolveRateFromExcelAndApi,
    validateParsedRow,
    downloadExcelTemplate as downloadTemplateUtil
} from './excel-order-import.utils';
import {
    OrderLinesDownloadOptions,
    downloadOrderLinesExcel as downloadOrderLinesExcelUtil
} from './order-lines-download.utils';

const CHUNK_SIZE = 10;

@Injectable({
    providedIn: 'root'
})
export class ExcelOrderImportService {
    constructor(private readonly itemDetailsService: ItemDetailsService) {}

    downloadTemplate(): void {
        downloadTemplateUtil();
    }

    downloadOrderLines(lines: any[], options: OrderLinesDownloadOptions): void {
        downloadOrderLinesExcelUtil(lines, options);
    }

    importRows(file: File, ctx: ExcelImportContext): Observable<ExcelImportResult> {
        return from(readFileAsArrayBuffer(file)).pipe(
            map((buffer) => parseExcelWorkbook(buffer)),
            concatMap((parsedRows) => this.enrichRows(parsedRows, ctx))
        );
    }

    private enrichRows(parsedRows: ParsedExcelRow[], ctx: ExcelImportContext): Observable<ExcelImportResult> {
        const skipped: ExcelSkippedRow[] = [];
        const validRows: ParsedExcelRow[] = [];

        for (const row of parsedRows) {
            const validationError = validateParsedRow(row);
            if (validationError) {
                skipped.push({ excelRow: row.excelRow, itno: row.itno || '', reason: validationError });
                continue;
            }
            validRows.push(row);
        }

        if (!validRows.length) {
            return of({ imported: [], skipped, stockWarnings: 0 });
        }

        const ledger = buildStockLedger(ctx.existingItemList);
        let lineNumber = ctx.startLineNumber;

        const chunks: ParsedExcelRow[][] = [];
        for (let i = 0; i < validRows.length; i += CHUNK_SIZE) {
            chunks.push(validRows.slice(i, i + CHUNK_SIZE));
        }

        return from(chunks).pipe(
            concatMap((chunk) => {
                const calls = chunk.map((row) =>
                    this.itemDetailsService
                        .getItemDetails(
                            ctx.company,
                            ctx.division,
                            {
                                itno: row.itno,
                                cuno: ctx.cuno,
                                whlo: ctx.whlo
                            },
                            'EXCEL_IMPORT'
                        )
                        .pipe(
                            catchError(() => of(null)),
                            map((details) => ({ parsed: row, details }))
                        )
                );

                return forkJoin(calls).pipe(
                    map((chunkResults) => {
                        const chunkImported: any[] = [];
                        for (const result of chunkResults) {
                            const { parsed, details } = result;
                            if (!details) {
                                skipped.push({
                                    excelRow: parsed.excelRow,
                                    itno: parsed.itno,
                                    reason: 'Item details could not be loaded.'
                                });
                                continue;
                            }

                            const statusError = getItemStatusRejectionMessage(details);
                            if (statusError) {
                                skipped.push({
                                    excelRow: parsed.excelRow,
                                    itno: parsed.itno,
                                    reason: statusError
                                });
                                continue;
                            }

                            const itemName = resolveItemNameFromItemDetails(details);
                            if (!itemName) {
                                skipped.push({
                                    excelRow: parsed.excelRow,
                                    itno: parsed.itno,
                                    reason: 'Item description not available from item details.'
                                });
                                continue;
                            }

                            const rate = resolveRateFromExcelAndApi(parsed.sapr, ctx.defaultPriceList, details.lstPriceList);
                            if (rate <= 0) {
                                skipped.push({
                                    excelRow: parsed.excelRow,
                                    itno: parsed.itno,
                                    reason: 'Could not resolve sales price (SAPR or default price list).'
                                });
                                continue;
                            }

                            const line = buildImportedOrderLine(parsed, lineNumber, itemName, rate, details, ctx);
                            lineNumber += 1;
                            const apiStockRaw = details?.ItemDetails?.itemStockCount;
                            const apiStock =
                                apiStockRaw !== undefined && apiStockRaw !== null ? Number(apiStockRaw) : null;
                            const stockExcluded = STOCK_CHECK_EXCLUDED_ITEMS.has(parsed.itno);
                            applyRunningStockCheck(line, apiStock, ledger, ctx.ruleActiveForDivision, stockExcluded);
                            chunkImported.push(line);
                        }
                        return chunkImported;
                    })
                );
            }),
            toArray(),
            map((chunkImports) => {
                const flatImported = chunkImports.flat();
                const stockWarnings = flatImported.filter((line) => !!line.copyOrderStockError).length;
                return {
                    imported: flatImported,
                    skipped,
                    stockWarnings
                };
            })
        );
    }
}
